import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { createOverviewStepContent } from "./segmentOverviewStepContent";
import { getOverviewTourSelection, SEGMENT_OVERVIEW_TOUR } from "./segmentOverviewTour";

import { buildTourSteps } from "@/features/tours/buildTourSteps";
import { TourRunner } from "@/features/tours/components/TourRunner";
import { useTour } from "@/features/tours/useTour";
import { useTourTargets } from "@/features/tours/useTourTarget";

type SegmentOverviewTourHostProps = {
  selectedId?: string;
  firstSegmentId?: string;
  onSelect: (segmentId: string | undefined) => void;
};

function RunningOverviewTour({ selectedId, firstSegmentId, onSelect }: SegmentOverviewTourHostProps) {
  const { t } = useTranslation();
  const { stepIds } = useTour();
  const targets = useTourTargets();

  const steps = useMemo(
    () =>
      buildTourSteps({
        definition: SEGMENT_OVERVIEW_TOUR,
        stepIds,
        t,
        getTarget: targets.get,
        getContent: createOverviewStepContent(t, stepIds),
      }),
    [stepIds, t, targets.get],
  );

  const prepareStep = useCallback(
    (stepId: string | undefined) => {
      const selection = getOverviewTourSelection(stepId, selectedId, firstSegmentId);
      if (selection === selectedId) return true;
      onSelect(selection);
      return false;
    },
    [selectedId, firstSegmentId, onSelect],
  );

  return <TourRunner steps={steps} prepareStep={prepareStep} />;
}

export function SegmentOverviewTourHost(props: SegmentOverviewTourHostProps) {
  const { isRunning } = useTour();
  if (!isRunning) return null;
  return <RunningOverviewTour {...props} />;
}
